import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { App } from '@df/core/app';
import { NPrivacyState, PrivacyStateService } from '@df/core/meta/privacy-state.service';
import { CookiesService } from '@df/core/service/cookies';
import { COOKIE_ID } from '@df/core/service/multisite';
import each from 'lodash-es/each';
import findKey from 'lodash-es/findKey';

@Injectable({
    providedIn: 'root'
})
export class PrivacyService {
    /**
     * Cookie matching rules to identify area of the cookie by its name
     */
    static readonly rules: Partial<Record<NPrivacyState.Area, Set<string | RegExp>>> = {
        [NPrivacyState.Area.core]: new Set([/^frontend/i, /^phpsessid/i, COOKIE_ID]),
        [NPrivacyState.Area.performance]: new Set([
            '_ga',
            '_gid',
            '_gaexp',
            '_gat',
            'AMP_TOKEN',
            /^__utm/,
            /^_gac_/,
            '__exponea_etc__',
            '_cs_s',
            '_cs_cvars',
            '_cs_ex',
            '_cs_c',
            '_cs_optout',
            '_cs_mk',
            '_cs_rl'
        ]),
        [NPrivacyState.Area.advertising]: new Set(['stc111474', 'rmStore', '_rmco', '_rmco_cs', '_rmco_jsv'])
    };

    /**
     * Default area of the cookie if no area is matched
     */
    static defaultArea = NPrivacyState.Area.personalization;

    /**
     * Services
     */
    private cookiesService = inject(CookiesService);
    private localStorageService = inject(LocalStorageService);
    private destroyRef = inject(DestroyRef);
    private privacyStateService = inject(PrivacyStateService);
    private app = inject(App);
    /**
     * Local storage key
     */
    private static readonly storageKey = '_prv_settings';

    /**
     * Initializes the privacy service
     */
    appInitialize() {
        if (!this.app.isPlatformBrowser) {
            return;
        }
        // load from storage
        this.loadStorage();

        // update status of the privacy state checking
        this.privacyStateService.setCheckStatus(this.isUserUpdateNeeded ? NPrivacyState.Status.modal : NPrivacyState.Status.ready);

        // save on changes
        this.privacyStateService.selection$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((selection: NPrivacyState.TSelection | undefined) => {
                if (selection) {
                    this.deleteRestrictedCookies();
                }
            });
    }

    /**
     * Checks if permissions confirmation is needed
     * (if any permission is not defined)
     */
    get isUserUpdateNeeded(): boolean {
        if (!this.app.isPlatformBrowser) {
            return false;
        }
        const storage = this.readStorage();
        return this.privacyStateService.getAreas().some(area => storage[area] === undefined);
    }

    /**
     * Allow all areas
     */
    allowAll() {
        this.privacyStateService.getAreas().forEach(area => {
            this.privacyStateService.setValue(area, true);
        });
        this.saveStorage();
    }

    /**
     * Allow all areas
     */
    allowCoreOnly() {
        this.privacyStateService.getAreas().forEach(area => {
            this.privacyStateService.setValue(area, area === NPrivacyState.Area.core);
        });
        this.saveStorage();
    }

    /**
     * Apply selection
     */
    apply(selection: Partial<NPrivacyState.TSelection>) {
        this.privacyStateService.getAreas().forEach(area => {
            this.privacyStateService.setValue(area, selection[area] ?? false);
        });
        this.saveStorage();
    }

    /**
     * Save privacy state to local storage
     */
    private saveStorage() {
        this.localStorageService.set(PrivacyService.storageKey, this.privacyStateService.getSelection());
        this.privacyStateService.setCheckStatus(NPrivacyState.Status.ready);
    }

    /**
     * Load privacy state from local storage
     */
    private loadStorage() {
        const data = this.readStorage();
        for (const area of this.privacyStateService.getAreas()) {
            const value = data[area];
            if (value !== undefined) {
                this.privacyStateService.getState(area).next(value);
            }
        }
    }

    /**
     * Read privacy state from local storage
     */
    private readStorage(): Partial<NPrivacyState.TSelection> {
        const value = this.localStorageService.get(PrivacyService.storageKey) as Partial<NPrivacyState.TSelection>;
        if (typeof value !== 'object') {
            return {};
        }
        return value;
    }

    /**
     * Deletes all cookies that are not allowed
     */
    protected deleteRestrictedCookies() {
        each(this.cookiesService.getAll(), (cookieValue: string, cookieName: string) => {
            // if this is matched cookie then take its area, default is personalization
            if (!this.privacyStateService.getValue(this.identifyCookieArea(cookieName))) {
                this.cookiesService.delete(cookieName);
                // this.logger.log('Cookie ' + cookieName + ' dropped cause of privacy restrictions');
            }
        });
    }

    /**
     * Retrieves area of the cookie
     */
    private identifyCookieArea(cookieName: string): NPrivacyState.Area {
        const key = findKey(PrivacyService.rules, (rules: Set<string | RegExp> | undefined) => {
            if (!rules) {
                return false;
            }
            for (const rule of rules.values()) {
                if (typeof rule === 'string') {
                    return rule === cookieName;
                }
                if (rule instanceof RegExp) {
                    return rule.test(cookieName);
                }
            }
            return false;
        });
        if (typeof key === 'string') {
            return Number(key) as NPrivacyState.Area;
        }
        return (key as NPrivacyState.Area | undefined) || PrivacyService.defaultArea;
    }
}
